/**
 * Subscription API Service
 * 
 * Handles all subscription-related API calls for both Stripe and Paystack.
 * This replaces the old multi-tier subscription system with a unified,
 * provider-agnostic approach.
 */

export default class Subscription {
  constructor(client) {
    this.client = client;
  }

  // ==========================================
  // STRIPE SUBSCRIPTION ENDPOINTS
  // ==========================================

  /**
   * Create a new Stripe subscription
   * @param {Object} data - Subscription data
   * @param {string|number} data.merchant_id - Merchant internal ID
   * @param {string} data.customer_id - Stripe customer ID (optional)
   * @param {string} data.price_id - Stripe price ID
   * @param {string} data.payment_method_id - Stripe payment method ID (optional)
   * @param {number} data.quantity - Quantity (optional)
   * @param {number} data.trial_days - Trial days (optional)
   * @param {string} data.promo_code - Promo code (optional, e.g., "WELCOM35")
   * @param {string} data.billing_cycle_anchor - ISO8601 or "now" (optional)
   * @param {string} idempotencyKey - Idempotency key for request
   */
  createStripeSubscription(data, idempotencyKey) {
    return this.client.post("/subscriptions", data, {
      headers: {
        "Idempotency-Key": idempotencyKey || this.generateIdempotencyKey(),
      },
    });
  }

  /**
   * Get a specific subscription by ID
   * @param {string|number} subscriptionId - Subscription ID
   */
  getSubscription(subscriptionId) {
    return this.client.get(`/subscriptions/${subscriptionId}`);
  }

  /**
   * Get the current active subscription for the merchant
   */
  getCurrentSubscription() {
    return this.client.get("/billing/subscriptions/current");
  }

  /**
   * List all subscriptions for the merchant
   * @param {number} page - Page number
   * @param {number} perPage - Items per page
   */
  listSubscriptions(page = 1, perPage = 20) {
    return this.client.get(`/billing/subscriptions?page=${page}&per_page=${perPage}`);
  }

  /**
   * Update a Stripe subscription
   * @param {string|number} subscriptionId - Subscription ID
   * @param {Object} data - Update data
   * @param {string} data.price_id - New price ID
   * @param {number} data.quantity - New quantity
   * @param {string} data.billing_cycle_anchor - Billing anchor
   * @param {string} data.promo_code - Promo code
   */
  updateSubscription(subscriptionId, data) {
    return this.client.patch(`/subscriptions/${subscriptionId}`, data);
  }

  /**
   * Cancel a Stripe subscription
   * @param {string|number} subscriptionId - Subscription ID
   * @param {boolean} immediately - Cancel immediately or at period end
   * @param {string} reason - Cancellation reason
   */
  cancelSubscription(subscriptionId, immediately = false, reason = null) {
    return this.client.post(`/billing/subscriptions/${subscriptionId}/cancel`, {
      immediately,
      reason,
    });
  }

  /**
   * Resume a cancelled subscription
   * @param {string|number} subscriptionId - Subscription ID
   */
  resumeSubscription(subscriptionId) {
    return this.client.post(`/subscriptions/${subscriptionId}/resume`);
  }

  /**
   * Reactivate a subscription
   * @param {string|number} subscriptionId - Subscription ID
   */
  reactivateSubscription(subscriptionId) {
    return this.client.post(`/billing/subscriptions/${subscriptionId}/reactivate`);
  }

  /**
   * Create a Stripe checkout session
   * @param {Object} data - Checkout data
   * @param {number} data.pricing_plan_id - Pricing plan ID
   * @param {string} data.promo_code - Promo code (optional)
   * @param {Object} data.addons - Additional addons
   * @param {string} data.success_url - Success redirect URL
   * @param {string} data.cancel_url - Cancel redirect URL
   */
  createCheckoutSession(data) {
    return this.client.post("/billing/subscriptions/checkout", data);
  }

  /**
   * Validate a promo code
   * @param {string} promoCode - Promo code to validate
   */
  validatePromoCode(promoCode) {
    return this.client.post("/billing/subscriptions/validate_promo", {
      promo_code: promoCode,
    });
  }

  /**
   * Get available subscription plans
   */
  getAvailablePlans() {
    return this.client.get("/billing/subscriptions/available_plans");
  }

  /**
   * Preview an upgrade
   * @param {string|number} subscriptionId - Subscription ID
   * @param {Object} params - Upgrade params
   */
  previewUpgrade(subscriptionId, params) {
    const queryString = new URLSearchParams(params).toString();
    return this.client.get(
      `/billing/subscriptions/${subscriptionId}/preview_upgrade?${queryString}`
    );
  }

  /**
   * Preview upcoming invoice
   * @param {string|number} subscriptionId - Subscription ID
   */
  previewInvoice(subscriptionId) {
    return this.client.get(`/billing/subscriptions/${subscriptionId}/preview_invoice`);
  }

  /**
   * Upgrade subscription to a new plan
   * @param {string|number} subscriptionId - Subscription ID
   * @param {Object} data - Upgrade data
   */
  upgradeSubscription(subscriptionId, data) {
    return this.client.post(`/billing/subscriptions/${subscriptionId}/upgrade`, data);
  }

  /**
   * Change subscription plan
   * @param {string|number} subscriptionId - Subscription ID
   * @param {Object} data - Plan change data
   */
  changePlan(subscriptionId, data) {
    return this.client.post(`/billing/subscriptions/${subscriptionId}/change_plan`, data);
  }

  /**
   * Add staff to subscription
   * @param {string|number} subscriptionId - Subscription ID
   * @param {number} quantity - Number of staff to add
   */
  addStaff(subscriptionId, quantity) {
    return this.client.post(`/billing/subscriptions/${subscriptionId}/add_staff`, {
      quantity,
    });
  }

  /**
   * Add branch to subscription
   * @param {string|number} subscriptionId - Subscription ID
   * @param {number} quantity - Number of branches to add
   */
  addBranch(subscriptionId, quantity) {
    return this.client.post(`/billing/subscriptions/${subscriptionId}/add_branch`, {
      quantity,
    });
  }

  /**
   * Add app addon to subscription
   * @param {string|number} subscriptionId - Subscription ID
   * @param {Object} data - Addon data
   */
  addAddon(subscriptionId, data) {
    return this.client.post(`/billing/subscriptions/${subscriptionId}/add_addon`, data);
  }

  // ==========================================
  // PAYSTACK SUBSCRIPTION ENDPOINTS
  // ==========================================

  /**
   * Initialize a Paystack subscription/payment
   * @param {Object} data - Payment data
   * @param {string|number} data.merchant_id - Merchant ID
   * @param {string} data.email - Payer email
   * @param {number} data.amount - Amount in smallest currency unit
   * @param {string} data.plan_id - Paystack plan ID
   * @param {string} data.callback_url - Callback URL
   * @param {string} data.promo_code - Promo code (optional)
   */
  initializePaystack(data) {
    return this.client.post("/billing/paystack/subscriptions/initialize", data);
  }

  /**
   * Verify a Paystack payment and create subscription
   * @param {string} reference - Payment reference
   */
  verifyPaystackPayment(reference) {
    return this.client.post("/billing/paystack/subscriptions/verify", { reference });
  }

  /**
   * Get available Paystack plans
   */
  getPaystackPlans() {
    return this.client.get("/billing/paystack/subscriptions/plans");
  }

  /**
   * Get Paystack customer details
   */
  getPaystackCustomer() {
    return this.client.get("/billing/paystack/subscriptions/customer");
  }

  /**
   * Upgrade Paystack subscription
   * @param {Object} data - Upgrade data
   */
  upgradePaystackSubscription(data) {
    return this.client.post("/billing/paystack/subscriptions/upgrade", data);
  }

  /**
   * Cancel Paystack subscription
   * Paystack requires a two-step flow with email token:
   * - First call with token=null triggers email to user
   * - Second call with token from email completes cancellation
   * @param {string|number} subscriptionId - Subscription ID
   * @param {string|null} token - Email token (null on first call, token string on second call)
   */
  cancelPaystackSubscription(subscriptionId, token = null) {
    return this.client.post(`/billing/paystack/subscriptions/${subscriptionId}/cancel`, {
      token,
    });
  }

  /**
   * Preview Paystack subscription upgrade (proration)
   * @param {Object} data - Upgrade preview data
   * @param {number} data.add_staff - Number of staff to add
   * @param {number} data.add_branches - Number of branches to add
   */
  previewPaystackUpgrade(data) {
    return this.client.post("/billing/paystack/subscriptions/upgrade", {
      ...data,
      preview: true,
    });
  }

  /**
   * Add staff to Paystack subscription
   * @param {string|number} subscriptionId - Subscription ID
   * @param {number} quantity - Number of staff
   */
  addPaystackStaff(subscriptionId, quantity) {
    return this.client.post(
      `/billing/paystack/subscriptions/${subscriptionId}/add_staff`,
      { quantity }
    );
  }

  /**
   * Add branches to Paystack subscription
   * @param {string|number} subscriptionId - Subscription ID
   * @param {number} quantity - Number of branches
   */
  addPaystackBranches(subscriptionId, quantity) {
    return this.client.post(
      `/billing/paystack/subscriptions/${subscriptionId}/add_branches`,
      { quantity }
    );
  }

  /**
   * Charge a Paystack subscription
   * @param {Object} data - Charge data
   */
  chargePaystackSubscription(data) {
    return this.client.post("/billing/paystack/subscriptions/charge", data);
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Generate a unique idempotency key
   */
  generateIdempotencyKey() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check if a currency is eligible for promo code WELCOM35
   * @param {string} currency - Currency code
   */
  isEligibleForWelcom35(currency) {
    const eligibleCurrencies = ["NGN", "GHS"];
    return eligibleCurrencies.includes(currency?.toUpperCase());
  }

  /**
   * Determine the payment provider based on user currency
   * @param {string} currency - User's currency code
   */
  getPaymentProvider(currency) {
    const paystackCurrencies = ["NGN", "GHS", "KES", "ZAR"];
    return paystackCurrencies.includes(currency?.toUpperCase())
      ? "paystack"
      : "stripe";
  }
}
